import { DateTime } from "luxon";

import { FormatType } from "../../../models/EbayOffer";
import { IPart } from "../../../models/Part";

export interface IListingScoreField {
  key: string;
  label: string;
  weight: number;
  present: boolean;
  applicable: boolean;
  fixable: boolean;
}

export interface IListingScore {
  present: number;
  total: number;
  percent: number;
  missing: string[];
  breakdown: IListingScoreField[];
}

// eBay favours fresh listings; anything older is penalised by search.
export const FRESH_AGE_DAYS = 30;
// How far the live price may drift from the intelligence average before it counts against the score.
export const PRICE_VARIANCE_THRESHOLD = 0.1;
// eBay rewards richer galleries; we want more than this many photos.
export const MIN_IMAGE_COUNT = 8;

const hasValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return value !== 0;
  return true;
};

const hasAspect = (part: IPart, name: string): boolean =>
  !!part.aspects?.some(
    (aspect) => aspect.name?.toLowerCase() === name.toLowerCase() && hasValue(aspect.value)
  );

const listingAgeDays = (part: IPart): number | null => {
  const source = part.offers?.[0]?.listingStarted ?? part.created;
  if (!source) return null;
  const dt = DateTime.fromISO(source, { zone: "utc" });
  if (!dt.isValid) return null;
  const days = Math.floor(DateTime.now().diff(dt, "days").days);
  return days < 0 ? 0 : days;
};

const livePrice = (part: IPart): number | null => {
  const offer = part.offers?.[0];
  if (!offer) return null;
  const price = offer.format === FormatType.Auction ? offer.auctionStartPrice : offer.buyItNowPrice;
  return price ?? null;
};

interface ICriterion {
  key: string;
  label: string;
  weight: number;
  fixable: boolean;
  applicable: (part: IPart) => boolean;
  present: (part: IPart) => boolean;
}

const CRITERIA: ICriterion[] = [
  { key: "MPN", label: "MPN", weight: 3, fixable: true, applicable: () => true, present: (p) => hasValue(p.partNumber) },
  { key: "OEMPN", label: "OE part number", weight: 3, fixable: true, applicable: () => true, present: (p) => hasValue(p.oePartNumber) },
  { key: "Age", label: `Listed < ${FRESH_AGE_DAYS} days`, weight: 3, fixable: false, applicable: (p) => listingAgeDays(p) != null, present: (p) => { const a = listingAgeDays(p); return a != null && a <= FRESH_AGE_DAYS; } },
  { key: "KType", label: "KType", weight: 2, fixable: true, applicable: () => true, present: (p) => hasValue(p.vehicle?.kType) },
  { key: "Brand", label: "Brand", weight: 2, fixable: true, applicable: () => true, present: (p) => hasAspect(p, "Brand") },
  { key: "Images", label: `More than ${MIN_IMAGE_COUNT} images`, weight: 2, fixable: false, applicable: (p) => Array.isArray(p.images), present: (p) => (p.images?.length ?? 0) > MIN_IMAGE_COUNT },
  {
    key: "Price",
    label: `Price within ${Math.round(PRICE_VARIANCE_THRESHOLD * 100)}% of intelligence`,
    weight: 2,
    fixable: false,
    applicable: (p) => p.intelligenceAverage != null && p.intelligenceAverage > 0 && (livePrice(p) ?? 0) > 0,
    present: (p) => {
      const intel = p.intelligenceAverage;
      const price = livePrice(p);
      if (intel == null || intel <= 0 || price == null || price <= 0) return false;
      return Math.abs(price - intel) / intel <= PRICE_VARIANCE_THRESHOLD;
    },
  },
  { key: "Grade", label: "Grade", weight: 1, fixable: true, applicable: () => true, present: (p) => hasValue(p.grade) },
  { key: "Make", label: "Make", weight: 1, fixable: true, applicable: () => true, present: (p) => hasValue(p.vehicle?.combinedMake) },
  { key: "Model", label: "Model", weight: 1, fixable: true, applicable: () => true, present: (p) => hasValue(p.vehicle?.combinedModel) },
  { key: "VIN", label: "VIN", weight: 1, fixable: true, applicable: () => true, present: (p) => hasValue(p.vehicle?.combinedVIN) },
];

export function computeListingScore(part: IPart): IListingScore {
  const missing: string[] = [];
  const breakdown: IListingScoreField[] = [];
  let present = 0;
  let total = 0;
  let presentWeight = 0;
  let totalWeight = 0;

  CRITERIA.forEach((criterion) => {
    const applicable = criterion.applicable(part);
    const isPresent = applicable && criterion.present(part);

    breakdown.push({
      key: criterion.key,
      label: criterion.label,
      weight: criterion.weight,
      present: isPresent,
      applicable,
      fixable: criterion.fixable,
    });

    if (!applicable) return;

    total += 1;
    totalWeight += criterion.weight;

    if (isPresent) {
      present += 1;
      presentWeight += criterion.weight;
    } else if (criterion.fixable) {
      missing.push(criterion.key);
    }
  });

  return {
    present,
    total,
    percent: totalWeight === 0 ? 0 : Math.round((presentWeight / totalWeight) * 100),
    missing,
    breakdown,
  };
}
